import { Component, OnInit, inject, ChangeDetectionStrategy, signal } from '@angular/core';
import {FormsModule} from '@angular/forms';
import {Router} from '@angular/router';
import {DatePipe, DecimalPipe} from '@angular/common';
import {MatAccordion, MatExpansionPanel, MatExpansionPanelDescription, MatExpansionPanelHeader, MatExpansionPanelTitle} from '@angular/material/expansion';
import {MatDatepicker, MatDatepickerInput, MatDatepickerToggle} from '@angular/material/datepicker';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {PartNumberDecoder} from '../model/part-number-decoder';
import {PrnUserSelection} from '../model/prn-user-selection';
import {PartNumberDecoderService} from '../services/part-number-decoder-service';
import {PersonLoginService} from '../services/person-login-service';
import {FileUtils} from '../utils/file-utils';
import {StringUtils} from '../utils/string-utils';

@Component({
  selector: 'part-number-decoder',
  templateUrl: './part-number-decoder.component.html',
  styleUrls: ['./part-number-decoder.component.css'],
  imports: [FormsModule, DecimalPipe, MatAccordion, MatExpansionPanel, MatExpansionPanelHeader, MatExpansionPanelTitle, MatExpansionPanelDescription, MatDatepicker, MatDatepickerInput, MatDatepickerToggle, MatProgressSpinner],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [DatePipe]
})
export class PartNumberDecoderComponent implements OnInit {
  private readonly personLoginService = inject(PersonLoginService);
  private readonly partNumberDecoderService = inject(PartNumberDecoderService);
  private readonly router = inject(Router);
  private readonly datePipe = inject(DatePipe);

  public partNumberInput!: string;
  public priceBasisDate: Date = new Date();
  public readonly decoding = signal<boolean>(false);
  public readonly exporting = signal<boolean>(false);
  public readonly errorMessage = signal<string>(undefined!);
  public readonly results = signal<PartNumberDecoder[]>(undefined!);

  ngOnInit(): void {
    if (!this.personLoginService.isInternal()) {
      this.router.navigate(['unauthorized']);
    }
  }

  public decode(): void {
    this.errorMessage.set(null!);
    this.results.set(null!);
    const partNumbers: string[] = this.parsePartNumbers();
    if (partNumbers.length === 0) {
      return;
    }
    const invalidPartNumber = partNumbers.find(partNumber => partNumber.length !== 15 && partNumber.length !== 30);
    if (invalidPartNumber) {
      this.errorMessage.set(invalidPartNumber + ' is invalid. A part number must be 15 characters long or 30 for paired SM/SG part number');
      return;
    }
    if (!this.priceBasisDate) {
      this.errorMessage.set('Please select a Price Basis Date');
      return;
    }
    const formattedPriceBasisDate = this.datePipe.transform(this.priceBasisDate, 'yyyy-MM-dd');
    this.decoding.set(true);
    this.partNumberDecoderService.decode(partNumbers, formattedPriceBasisDate!).subscribe({
      next: (results: PartNumberDecoder[]) => {
        this.results.set(results || []);
        this.decoding.set(false);
      },
      error: (error) => {
        this.errorMessage.set(StringUtils.getErrorMessage(error));
        this.decoding.set(false);
      }
    });
  }

  public exportToExcel(): void {
    if (!this.results()?.length) {
      return;
    }
    this.errorMessage.set(null!);
    this.exporting.set(true);
    this.partNumberDecoderService.exportToExcel(this.results()).subscribe({
      next: (blob: Blob) => {
        FileUtils.downloadFile(blob, 'PartNumberDecoder.xlsx');
        this.exporting.set(false);
      },
      error: (error) => {
        this.errorMessage.set(StringUtils.getErrorMessage(error));
        this.exporting.set(false);
      }
    });
  }

  private parsePartNumbers(): string[] {
    if (StringUtils.isBlank(this.partNumberInput)) {
      return [];
    }
    return this.partNumberInput.split(/[\n,]/)
      .map(partNumber => partNumber.trim())
      .filter(partNumber => partNumber.length > 0);
  }

  public prnDigits(result: PartNumberDecoder): string[] {
    const selections = result?.partNumberDecodeResult?.prnDigitUserSelections;
    if (!selections) {
      return [];
    }
    // Combined digits like 0506 are sorted by their starting digit (05)
    return Object.keys(selections).sort((first, second) =>
      first.substring(0, 2).localeCompare(second.substring(0, 2), undefined, {numeric: true, sensitivity: 'base'})
      || first.localeCompare(second, undefined, {numeric: true, sensitivity: 'base'}));
  }

  public getSelection(result: PartNumberDecoder, prnDigit: string): PrnUserSelection {
    return result.partNumberDecodeResult.prnDigitUserSelections[prnDigit];
  }

  public formatModelType(modelType: string): string {
    return modelType ? modelType.replace(/DASH/g, '-') : modelType;
  }

  public getTotalPrice(result: PartNumberDecoder): number {
    const skus = result?.configurationData?.skus;
    if (!skus) {
      return 0;
    }
    return skus.reduce((total, sku) => total + (sku.price || 0), 0);
  }
}
